"use client";

import { StatsTable } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { mapStandingsToTable, DEFAULT_COLUMNS } from "@/utilities/mappers/standings";
import type { StandingsGroup } from "@/utilities/data/types";

interface StandingsSectionProps {
    groups: StandingsGroup[];
}

/**
 * Full standings page — one table per group, stacked. Reuses the same
 * `mapStandingsToTable` + `StatsTable` pipeline the standings view already
 * uses for international competitions.
 */
export function StandingsSection({ groups }: StandingsSectionProps) {
    const labels = useLabels();
    const nav = useNavigation();

    return (
        <div className="max-w-350 mx-auto px-4 py-6 space-y-8">
            {groups.map((g) => (
                <GroupTable key={g.id ?? g.name ?? ""} group={g} labels={labels} teamHref={nav.team} />
            ))}
        </div>
    );
}

function GroupTable({
    group,
    labels,
    teamHref,
}: {
    group: StandingsGroup;
    labels: ReturnType<typeof useLabels>;
    teamHref: (teamId: string) => string;
}) {
    const pseudoStandings = { entries: group.entries, metadata: { legend: [] } };
    // `form` isn't populated on Nations League standings — drop the column so we
    // don't render an empty last cell on every row.
    const columns = DEFAULT_COLUMNS.filter((c) => c !== "form");
    const table = mapStandingsToTable(pseudoStandings as never, labels.standings, columns, undefined, teamHref);
    return (
        <section>
            <h2 className="text-lg font-bold mb-3">{group.name ?? ""}</h2>
            <StatsTable
                columns={table.columns}
                rows={table.rows}
                styles={{
                    borderRadius: "md",
                    shadow: "md",
                    typography: {
                        cell: "text-sm font-medium text-foreground",
                        header: "text-xs uppercase font-bold text-muted-foreground",
                    },
                }}
            />
        </section>
    );
}
